/**
 * ShareCard — flomo-style minimal share card for a completed segment.
 *
 * v1: pure DOM-rendered card + "save image" via html2canvas (loaded lazily).
 * No server image host, no Web Share API — just download the PNG.
 *
 * The QR code points to starter.cyberunion.net (no per-user ref tracking yet;
 * that needs a server-side invite code mint endpoint to do safely).
 */
import { useEffect, useRef, useState } from 'react';
import type { ActiveSegment } from '../state/segment';
import { cardStatusFromUserStatus } from '../state/segment';

interface ShareCardProps {
  segment: ActiveSegment;
}

const SHARE_TARGET_URL = 'https://starter.cyberunion.net';

interface SaveFilePickerOptions {
  suggestedName?: string;
  types?: Array<{
    description: string;
    accept: Record<string, string[]>;
  }>;
}

interface FileSystemWritableFileStream {
  write(data: Blob): Promise<void>;
  close(): Promise<void>;
}

interface FileSystemFileHandle {
  createWritable(): Promise<FileSystemWritableFileStream>;
}

interface SavePickerWindow extends Window {
  showSaveFilePicker?: (options?: SaveFilePickerOptions) => Promise<FileSystemFileHandle>;
}

export function ShareCard({ segment }: ShareCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const exportedImageUrlRef = useRef<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);
  const [exportedImageUrl, setExportedImageUrl] = useState<string | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);

  // Compute outcome counts per card (latest trace wins).
  const stats = { done: 0, in_progress: 0, dropped: 0, pending: 0 };
  for (const card of segment.cards) {
    const last = [...segment.traces].reverse().find((t) => t.cardId === card.id);
    const finalStatus = last ? cardStatusFromUserStatus(last.userStatus) : 'pending';
    stats[finalStatus]++;
  }
  const doneCards = segment.cards.filter((card) => {
    const last = [...segment.traces].reverse().find((t) => t.cardId === card.id);
    return last && last.userStatus === 'done';
  });
  const inProgressCards = segment.cards.filter((card) => {
    const last = [...segment.traces].reverse().find((t) => t.cardId === card.id);
    return last && last.userStatus === 'in_progress';
  });
  const roundCount = segment.traces.length;
  const totalMinutes = roundCount * 5;
  const touchedCount = new Set(segment.traces.map((trace) => trace.cardId)).size;

  // Lazy-load QR code generation. Keep it offline (no third-party API).
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const mod = await import('qrcode');
        const url = await mod.toDataURL(SHARE_TARGET_URL, {
          margin: 0,
          width: 120,
          color: { dark: '#1a2533', light: '#ffffff' },
        });
        if (!cancelled) setQrDataUrl(url);
      } catch (err) {
        console.warn('[ShareCard] qrcode load failed', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    return () => {
      if (exportedImageUrlRef.current) URL.revokeObjectURL(exportedImageUrlRef.current);
    };
  }, []);

  async function saveImage() {
    if (!cardRef.current || saving) return;
    setSaving(true);
    setSaveStatus(null);
    try {
      const mod = await import('html2canvas');
      const html2canvas = mod.default;
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
      });
      const blob: Blob | null = await new Promise((resolve) =>
        canvas.toBlob((b) => resolve(b), 'image/png'),
      );
      if (!blob) throw new Error('canvas.toBlob returned null');
      if (exportedImageUrlRef.current) URL.revokeObjectURL(exportedImageUrlRef.current);
      const objectUrl = URL.createObjectURL(blob);
      exportedImageUrlRef.current = objectUrl;
      setExportedImageUrl(objectUrl);
      const ts = new Date(segment.startedAt).toISOString().slice(0, 10);
      const fileName = `daily-starter-${ts}.png`;
      const picker = (window as SavePickerWindow).showSaveFilePicker;
      if (picker) {
        const handle = await picker({
          suggestedName: fileName,
          types: [
            {
              description: 'PNG 图片',
              accept: { 'image/png': ['.png'] },
            },
          ],
        });
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
        setSaveStatus({
          kind: 'success',
          text: `图片已保存到你选择的位置:${fileName}。`,
        });
        return;
      }

      const link = document.createElement('a');
      link.download = fileName;
      link.href = objectUrl;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setSaveStatus({
        kind: 'success',
        text: `当前浏览器不支持选择保存位置,图片已按浏览器规则下载:${fileName}。也可以点下面的链接打开图片。`,
      });
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        setSaveStatus({
          kind: 'error',
          text: '你刚刚取消了保存,图片没有写入文件。需要的话可以再点一次选择保存位置。',
        });
        setSaving(false);
        return;
      }
      console.warn('[ShareCard] save failed', err);
      setSaveStatus({
        kind: 'error',
        text: '保存失败。可以再点一次,或者先截图这张分享卡。',
      });
    } finally {
      setSaving(false);
    }
  }

  const dateStr = new Date(segment.startedAt).toLocaleDateString('zh-CN');

  return (
    <div className="share-card-wrap">
      <div ref={cardRef} className="share-card-canvas">
        <header className="share-card-header">
          <strong>每日启动器</strong>
          <span className="share-card-date">{dateStr}</span>
        </header>
        <p className="share-card-kicker">今天没有停在开始前</p>
        <p className="share-card-tagline">
          用 {totalMinutes} 分钟推进 {touchedCount} 件事。不是完美的一天,但已经不是空白的一天。
        </p>

        <div className="share-card-stats">
          <span>{roundCount} 轮 5 分钟</span>
          {stats.done > 0 && <span>· 完成 {stats.done} 张</span>}
          {stats.in_progress > 0 && <span>· 还在继续 {stats.in_progress} 张</span>}
          {stats.dropped > 0 && <span>· 已放下 {stats.dropped} 张</span>}
        </div>

        {doneCards.length > 0 && (
          <div className="share-card-section">
            <span className="share-card-section-label">已完成的卡片</span>
            <ul>
              {doneCards.map((c) => (
                <li key={c.id}>{c.title}</li>
              ))}
            </ul>
          </div>
        )}

        {inProgressCards.length > 0 && (
          <div className="share-card-section">
            <span className="share-card-section-label">还在继续的卡片</span>
            <ul>
              {inProgressCards.map((c) => (
                <li key={c.id}>{c.title}</li>
              ))}
            </ul>
          </div>
        )}

        <footer className="share-card-footer">
          <img className="share-card-mascot" src="/mascot/gime5-ufo-7.png" alt="赛博小U" />
          {qrDataUrl && <img className="share-card-qr" src={qrDataUrl} alt="每日启动器二维码" />}
          <div className="share-card-footer-text">
            <span>扫码体验每日启动器</span>
            <span className="muted tiny">让赛博小U陪你完成今天第一步</span>
            <span className="share-card-domain">starter.cyberunion.net</span>
          </div>
        </footer>
      </div>

      <div className="actions share-card-actions">
        <button className="btn-lime" type="button" onClick={saveImage} disabled={saving}>
          {saving ? '正在生成图片…' : saveStatus?.kind === 'success' ? '重新选择位置保存' : '选择位置保存图片'}
        </button>
      </div>
      {saveStatus && (
        <div className={`share-save-status ${saveStatus.kind}`} role="status" aria-live="polite">
          <strong>{saveStatus.kind === 'success' ? '已生成图片' : '保存没有成功'}</strong>
          <span>{saveStatus.text}</span>
          {saveStatus.kind === 'success' && exportedImageUrl && (
            <a href={exportedImageUrl} download={`daily-starter-${dateStr.replace(/\//g, '-')}.png`}>
              打开生成的图片
            </a>
          )}
        </div>
      )}
    </div>
  );
}
